import { defineStore } from "pinia";
import { useRuntimeConfig } from "nuxt/app";
import { SignJWT, jwtVerify } from "jose";
import { useApiCall } from "@/composables/useApiCall";
import type {
  SocialType,
  SocialAccount,
  SocialPage,
  SocialTableData,
  SocialAccountAttributes,
  SocialSorting,
  SocialMonitoringType,
} from "@/types/SocialType";
import { mapSocialToPictureUrl } from "@/utils/socialHelper";

interface SocialState {
  accounts: SocialAccount[];
  pages: SocialPage[];
  selectedSocialTab: SocialType;
  selectedSocialSubTab: string;
  isConnectingAccount: boolean;
  isConnectingPage: boolean;
  sorting: SocialSorting | null;
  searchTerm: string;
  isDeletingAccount: boolean;
  isDeletingPage: boolean;
  showAddSourceDialog: boolean;
  isSelectedExistingAccount: boolean;
  mediaSource: SocialMonitoringType | null;
  deletedAccountPage: { type: "account" | "page"; name: string } | null;
  hasNoFacebookAccountConnected: boolean;
}

interface TokenPayload {
  iss: string;
  iat: number;
  exp: number;
  type: string;
  sub_type: string;
  active_tab: string;
  from?: string;
}

function accountFilterCallback(
  account: SocialAccount,
  providerType: SocialType,
) {
  if (providerType === "youtube") {
    return account?.attributes?.provider_type === "youtube";
  }
  return (
    ["facebook", "instagram"].includes(account?.attributes?.provider_type) ||
    account?.attributes?.provider_type === providerType
  );
}

function pageFilterCallback(
  page: SocialPage,
  providerType: SocialType,
  sourceType: string = "page",
) {
  return (
    page?.attributes?.provider_type === providerType &&
    page?.attributes?.source_type === sourceType
  );
}

function accountsSortCallback(
  a: SocialAccount,
  b: SocialAccount,
  key: SocialSorting["key"],
  order: SocialSorting["order"],
) {
  let comparison = 0;
  if (key === "name") {
    comparison = a.attributes.account_name.localeCompare(
      b.attributes.account_name,
    );
  } else if (key === "addedOn") {
    comparison =
      new Date(a.attributes.created_at).getTime() -
      new Date(b.attributes.created_at).getTime();
  }
  return order === "asc" ? comparison : -comparison;
}

function pagesSortCallback(
  a: SocialPage,
  b: SocialPage,
  key: SocialSorting["key"],
  order: SocialSorting["order"],
) {
  let comparison = 0;
  if (key === "name") {
    comparison = a.attributes.page_name.localeCompare(b.attributes.page_name);
  } else if (key === "addedOn") {
    comparison =
      new Date(a.attributes.created_at).getTime() -
      new Date(b.attributes.created_at).getTime();
  }
  return order === "asc" ? comparison : -comparison;
}

function includesSearchTerm(text: string, searchTerm: string): boolean {
  return text.toLowerCase().includes(searchTerm.toLowerCase());
}

export const useSocialStore = defineStore("social", {
  state: (): SocialState => ({
    accounts: [],
    pages: [],
    selectedSocialTab: "facebook",
    selectedSocialSubTab: "pages",
    isConnectingAccount: false,
    isConnectingPage: false,
    sorting: null,
    searchTerm: "",
    isDeletingAccount: false,
    isDeletingPage: false,
    showAddSourceDialog: false,
    isSelectedExistingAccount: false,
    mediaSource: null,
    deletedAccountPage: null,
    hasNoFacebookAccountConnected: false,
  }),

  getters: {
    accountCount: (state): number => state.accounts.length,

    selectedSocialAccounts: (state): SocialAccount[] => {
      return state.accounts.filter((account) =>
        accountFilterCallback(account, state.selectedSocialTab),
      );
    },

    selectedSocialAccountsCount: (state): number => {
      return state.accounts.filter((account) =>
        accountFilterCallback(account, state.selectedSocialTab),
      ).length;
    },

    selectedPages: (state): SocialPage[] => {
      return state.pages.filter((page) =>
        pageFilterCallback(page, state.selectedSocialTab),
      );
    },

    selectedPagesCount(state): number {
      return state.pages.filter((page) =>
        pageFilterCallback(page, state.selectedSocialTab),
      ).length;
    },

    selectedHashtagsCount(state): number {
      return state.pages.filter((page) =>
        pageFilterCallback(page, state.selectedSocialTab, "hashtag"),
      ).length;
    },

    socialTableData(state): SocialTableData[] {
      if (["pages", "hashtag"].includes(state.selectedSocialSubTab)) {
        let filteredPages = state.pages.filter((page) =>
          pageFilterCallback(
            page,
            state.selectedSocialTab,
            state.selectedSocialSubTab.includes("page") ? "page" : "hashtag",
          ),
        );
        if (state.searchTerm.trim() !== "") {
          filteredPages = filteredPages.filter((page) =>
            includesSearchTerm(
              page.attributes.page_name,
              state.searchTerm.trim(),
            ),
          );
        }
        if (state.sorting) {
          filteredPages = filteredPages.sort((a, b) =>
            pagesSortCallback(a, b, state.sorting!.key, state.sorting!.order),
          );
        }
        return filteredPages.map((item) => ({
          pic: mapSocialToPictureUrl(item.attributes) || "", // No image, fallback to first letter
          name: item.attributes.page_name,
          addedBy: item.attributes.added_by,
          addedOn: item.attributes.created_at,
          id: item.id,
        }));
      } else if (state.selectedSocialSubTab === "accounts") {
        let filteredAccounts = state.accounts.filter((account) =>
          accountFilterCallback(account, state.selectedSocialTab),
        );
        if (state.searchTerm.trim() !== "") {
          filteredAccounts = filteredAccounts.filter((account) =>
            includesSearchTerm(
              account.attributes.account_name,
              state.searchTerm.trim(),
            ),
          );
        }
        if (state.sorting) {
          filteredAccounts.sort((a, b) =>
            accountsSortCallback(
              a,
              b,
              state.sorting!.key,
              state.sorting!.order,
            ),
          );
        }
        return filteredAccounts.map((item) => ({
          pic: item.attributes.profile_pic || "", // No image, fallback to first letter
          name: item.attributes.account_name,
          addedBy: item.attributes.added_by,
          addedOn: item.attributes.created_at,
          id: item.id,
        }));
      }
      return [];
    },
    allSocialPagesCount(state) {
      return state.pages.reduce(
        (acc, cur) => {
          if (cur?.attributes?.provider_type === "facebook") {
            acc.facebook_pages = (acc.facebook_pages || 0) + 1;
          } else if (cur?.attributes?.provider_type === "instagram") {
            if (cur?.attributes?.source_type === "page") {
              acc.instagram_pages = (acc.instagram_pages || 0) + 1;
            } else {
              acc.instagram_hashtag = (acc.instagram_hashtag || 0) + 1;
            }
          } else if (cur?.attributes?.provider_type === "twitter") {
            acc.twitter_pages = (acc.twitter_pages || 0) + 1;
          } else if (
            cur?.attributes?.provider_type === "google" ||
            cur?.attributes?.provider_type === "youtube"
          ) {
            acc.youtube_pages = (acc.youtube_pages || 0) + 1;
          }
          return acc;
        },
        {
          facebook_pages: 0,
          instagram_pages: 0,
          instagram_hashtag: 0,
          twitter_pages: 0,
          youtube_pages: 0,
        },
      ) as {
        facebook_pages: number;
        instagram_pages: number;
        instagram_hashtag: number;
        twitter_pages: number;
        youtube_pages: number;
      };
    },
    allSocialAccounts(state) {
      return state.accounts.reduce(
        (acc, cur) => {
          if (
            cur?.attributes?.provider_type === "facebook" ||
            cur?.attributes?.provider_type === "instagram"
          ) {
            acc.facebook.push(cur.attributes);
          } else if (cur?.attributes?.provider_type === "youtube") {
            acc.youtube.push(cur.attributes);
          }
          return acc;
        },
        {
          facebook: [],
          instagram: [],
          youtube: [],
        } as {
          facebook: SocialAccountAttributes[];
          instagram: SocialAccountAttributes[];
          youtube: SocialAccountAttributes[];
        },
      );
    },
  },

  actions: {
    async fetchAccounts() {
      const accountRes = await useApiCall("/social_accounts");
      if (accountRes?.data?.value?.data?.length > 0) {
        this.accounts = accountRes.data.value.data as SocialAccount[];
      }
    },

    async fetchPages() {
      const pageRes = await useApiCall(`/social_pages`);
      if (pageRes?.data?.value?.data?.length > 0) {
        this.pages = pageRes.data.value.data as SocialPage[];
      }
    },

    async fetchAccountsAndPages() {
      await this.fetchAccounts();
      await this.fetchPages();
    },

    async connectAccount({
      accountName,
      accountId,
      type,
      profilePic,
    }: {
      accountName: string;
      accountId: string;
      type: string;
      profilePic: string | undefined;
    }) {
      this.isConnectingAccount = true;
      const body = {
        social_account: {
          account_id: accountId,
          account_name: accountName,
          provider_type: type,
          profile_pic: profilePic || "",
        },
      };
      try {
        const accountRes = await useApiCall("/social_accounts", {
          method: "POST",
          body,
        });

        // If already account create
        if (accountRes?.error?.value?.data?.errors?.length > 0) {
          if (accountRes?.error?.value?.data?.errors[0]?.code === "taken") {
            return;
          }
          throw new Error(
            accountRes?.error?.value?.data?.errors[0]?.detail ||
              "Failed to create account",
          );
        }
        if (accountRes?.data?.value?.data?.id) {
          this.accounts.push(accountRes.data.value.data);
        } else {
          throw new Error("Failed to create account");
        }
        /* eslint-disable no-useless-catch */
      } catch (error) {
        throw error;
      } finally {
        /* eslint-enable no-useless-catch */
        this.isConnectingAccount = false;
      }
    },
    async connectPage(
      source: string,
      sourceType: string,
      providerType?: string,
    ): Promise<string | undefined> {
      this.isConnectingPage = true;
      const accountId = providerType
        ? this.accounts.find((account) =>
            accountFilterCallback(account, providerType as SocialType),
          )?.id
        : undefined;
      const body = {
        social_page: {
          source,
          source_type: sourceType,
          // Twitter pages are tracked independently without account association
          // (business rule: do not associate social_account_id for Twitter)
          ...(accountId && providerType !== "twitter"
            ? { social_account_id: accountId }
            : {}),
          ...(providerType ? { provider_type: providerType } : {}),
        },
      };
      try {
        const socialPageRes = await useApiCall("/social_pages", {
          method: "POST",
          body,
        });
        // If request failed
        if (socialPageRes.error.value) {
          const err = socialPageRes.error.value;
          throw new Error(
            err.data?.errors?.[0]?.detail ||
              err.message ||
              "Failed to connect page",
          );
        }
        if (socialPageRes?.data?.value?.data?.id) {
          this.pages.push(socialPageRes.data.value.data);
          return socialPageRes.data.value.data.attributes.page_name;
        } else {
          throw new Error("Failed to connect page");
        }
        /* eslint-disable no-useless-catch */
      } catch (error) {
        throw error;
      } finally {
        /* eslint-enable no-useless-catch */
        this.isConnectingPage = false;
      }
    },

    async deleteAccount(accountId: string) {
      try {
        this.isDeletingAccount = true;
        await useApiCall(`/social_accounts/${accountId}`, {
          method: "DELETE",
        });
        const account = this.accounts.find(
          (acc) => String(acc.id) === String(accountId),
        );
        this.accounts.splice(
          0,
          this.accounts.length,
          ...this.accounts.filter(
            (acc) => String(acc.id) !== String(accountId),
          ),
        );
        this.pages.splice(
          0,
          this.pages.length,
          ...this.pages.filter(
            (page) =>
              String(page.attributes.social_account_id) !== String(accountId),
          ),
        );
        this.deletedAccountPage = {
          type: "account",
          name: account?.attributes.account_name || "",
        };
        /* eslint-disable no-useless-catch */
      } catch (error) {
        throw error;
      } finally {
        /* eslint-enable no-useless-catch */
        this.isDeletingAccount = false;
      }
    },

    async deletePage(pageId: string) {
      try {
        this.isDeletingPage = true;
        await useApiCall(`/social_pages/${pageId}`, {
          method: "DELETE",
        });
        const page = this.pages.find((p) => String(p.id) === String(pageId));
        this.pages.splice(
          0,
          this.pages.length,
          ...this.pages.filter((page) => String(page.id) !== String(pageId)),
        );
        this.deletedAccountPage = {
          type: "page",
          name: page?.attributes.page_name || "",
        };
        /* eslint-disable no-useless-catch */
      } catch (error) {
        throw error;
      } finally {
        /* eslint-enable no-useless-catch */
        this.isDeletingPage = false;
      }
    },

    resetSorting() {
      this.sorting = null;
    },
    setSorting(key: SocialSorting["key"], order: SocialSorting["order"]) {
      this.sorting = { key, order };
    },

    resetOnTabChange() {
      this.sorting = null;
      this.searchTerm = "";
      this.selectedSocialSubTab = "pages";
    },

    async authenticateWithDigimind(
      socialType: string,
      socialSubType: string,
      from?: string,
    ) {
      const config = useRuntimeConfig();
      const JWT_SECRET = config.public.DIGIMIND_JWT_SECRET as string;
      const now = Math.floor(Date.now() / 1000); // current time in seconds

      // We are generating a JWT token valid for 15 minutes, which we send as a payload to the Digimind redirect URL for social login. The same token is sent back to us after a successful login for validation.
      const payload: TokenPayload = {
        iss: window.location.host,
        iat: now,
        exp: now + 15 * 60, // 15 minutes later
        type: socialType,
        sub_type: socialSubType,
        active_tab: this.selectedSocialTab,
        ...(from && { from }),
      };

      const secret = new TextEncoder().encode(JWT_SECRET);

      const token = await new SignJWT(payload as any)
        .setProtectedHeader({ alg: "HS256" })
        .sign(secret);

      // redirect
      window.location.href = `${(config.public.DIGIMIND_SOCIAL_URL as string).replace("<SOCIAL_TYPE>", socialType.toUpperCase())}?token=${token}`;
    },

    async isValidRedirectParams(
      params: Record<string, string | string[] | undefined>,
    ): Promise<[boolean, { msg: string; param?: string }]> {
      const requiredParams = [
        "state",
        "account_name",
        "account_id",
        "provider_type",
      ];

      for (const param of requiredParams) {
        const value = params[param];
        if (
          !value ||
          Array.isArray(value) ||
          (typeof value === "string" && value.trim() === "")
        ) {
          return [false, { msg: "social.missing-params-digimind", param }];
        }
      }
      try {
        const config = useRuntimeConfig();
        const JWT_SECRET = config.public.DIGIMIND_JWT_SECRET as string;
        const secret = new TextEncoder().encode(JWT_SECRET);

        const token = params.state as string;
        await jwtVerify(token, secret);
      } catch {
        return [false, { msg: "social.invalid-digimind-token" }];
      }

      return [true, { msg: "social.valid-digimind-params-msg" }];
    },

    async decodeSocialToken(token: string) {
      const config = useRuntimeConfig();
      const JWT_SECRET = config.public.DIGIMIND_JWT_SECRET as string;
      const secret = new TextEncoder().encode(JWT_SECRET);
      const decoded = await jwtVerify(token, secret);
      return decoded.payload as unknown as TokenPayload;
    },

    setStateAfterSocialLoginRedirect(payload: TokenPayload) {
      this.showAddSourceDialog = true;
      this.isSelectedExistingAccount = true;
      this.mediaSource = payload.sub_type as SocialMonitoringType;
      this.selectedSocialTab = payload.active_tab as SocialType;
    },

    async checkAccountsForFacebookInstagram() {
      await this.fetchAccounts();
      this.hasNoFacebookAccountConnected = this.accounts.every(
        (account) =>
          account.attributes.provider_type !== "facebook" &&
          account.attributes.provider_type !== "instagram",
      );
    },
  },
});
